import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const PoliticaDePrivacidade = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para o início
        </Link>
        
        <Card className="shadow-lg">
          <CardHeader className="border-b bg-gradient-to-r from-teal-700 to-teal-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-3 text-2xl">
              <Shield className="h-6 w-6" />
              Política de Privacidade
            </CardTitle>
            <p className="text-teal-100 mt-2">Última atualização: {new Date().toLocaleDateString('pt-BR')}</p>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none p-8">
            <h2 className="text-xl font-bold text-slate-800 mt-0">1. Introdução</h2>
            <p className="text-slate-600">
              A CERP Brasil NR1 ("nós", "nosso" ou "Plataforma") está comprometida em proteger a 
              privacidade dos dados de nossos usuários. Esta Política de Privacidade descreve como 
              coletamos, usamos, armazenamos e protegemos suas informações pessoais em conformidade 
              com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
            </p>

            <h2 className="text-xl font-bold text-slate-800">2. Dados Coletados</h2>
            <p className="text-slate-600">Coletamos os seguintes tipos de dados:</p>
            
            <h3 className="text-lg font-semibold text-slate-700">2.1 Dados de Cadastro</h3>
            <ul className="text-slate-600">
              <li>Nome completo</li>
              <li>Email corporativo</li>
              <li>Telefone de contato</li>
              <li>Dados da empresa (CNPJ, Razão Social, Endereço)</li>
              <li>Cargo e função</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-700">2.2 Dados de Funcionários</h3>
            <ul className="text-slate-600">
              <li>Nome, CPF, data de nascimento</li>
              <li>Cargo, setor e função</li>
              <li>Dados de saúde ocupacional (CID de afastamentos)</li>
              <li>Histórico de treinamentos</li>
              <li>Exposição a riscos ocupacionais</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-700">2.3 Dados de Navegação</h3>
            <ul className="text-slate-600">
              <li>Endereço IP</li>
              <li>Tipo de navegador e dispositivo</li>
              <li>Páginas visitadas e tempo de permanência</li>
              <li>Cookies e tecnologias similares</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-800">3. Finalidade do Tratamento</h2>
            <p className="text-slate-600">Utilizamos seus dados para:</p>
            <ul className="text-slate-600">
              <li>Fornecer e melhorar nossos serviços de SST</li>
              <li>Gerar relatórios e documentos legais (PGR, AEP, etc.)</li>
              <li>Comunicações sobre o serviço e suporte técnico</li>
              <li>Cumprimento de obrigações legais e regulatórias</li>
              <li>Análises estatísticas e melhorias da plataforma</li>
              <li>Prevenção de fraudes e segurança</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-800">4. Base Legal</h2>
            <p className="text-slate-600">
              O tratamento de dados é realizado com base nas seguintes hipóteses legais da LGPD:
            </p>
            <ul className="text-slate-600">
              <li><strong>Consentimento:</strong> Para finalidades específicas informadas ao usuário</li>
              <li><strong>Execução de Contrato:</strong> Para prestação dos serviços contratados</li>
              <li><strong>Obrigação Legal:</strong> Para cumprimento de normas trabalhistas e de SST</li>
              <li><strong>Legítimo Interesse:</strong> Para melhorias e segurança da plataforma</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-800">5. Compartilhamento de Dados</h2>
            <p className="text-slate-600">
              Seus dados podem ser compartilhados com:
            </p>
            <ul className="text-slate-600">
              <li>Prestadores de serviços essenciais (hospedagem, email, pagamentos)</li>
              <li>Órgãos governamentais quando exigido por lei (e-Social, etc.)</li>
              <li>Parceiros de integração autorizados pelo usuário</li>
            </ul>
            <p className="text-slate-600">
              Não vendemos ou alugamos dados pessoais a terceiros para fins de marketing.
            </p>

            <h2 className="text-xl font-bold text-slate-800">6. Segurança dos Dados</h2>
            <p className="text-slate-600">
              Implementamos medidas técnicas e organizacionais para proteger seus dados, incluindo:
            </p>
            <ul className="text-slate-600">
              <li>Criptografia de dados em trânsito (HTTPS/TLS)</li>
              <li>Controle de acesso baseado em funções</li>
              <li>Backup regular e recuperação de desastres</li>
              <li>Monitoramento de segurança e logs de auditoria</li>
              <li>Treinamento de equipe em proteção de dados</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-800">7. Retenção de Dados</h2>
            <p className="text-slate-600">
              Os dados são mantidos pelo tempo necessário para cumprir as finalidades descritas, 
              respeitando os prazos legais de guarda estabelecidos pela legislação trabalhista e 
              tributária brasileira (geralmente 5 a 20 anos, conforme o tipo de documento).
            </p>

            <h2 className="text-xl font-bold text-slate-800">8. Direitos do Titular</h2>
            <p className="text-slate-600">
              Conforme a LGPD, você tem direito a:
            </p>
            <ul className="text-slate-600">
              <li>Confirmar a existência de tratamento de dados</li>
              <li>Acessar seus dados pessoais</li>
              <li>Corrigir dados incompletos ou desatualizados</li>
              <li>Solicitar anonimização ou exclusão de dados desnecessários</li>
              <li>Portabilidade dos dados para outro fornecedor</li>
              <li>Revogar consentimento a qualquer momento</li>
              <li>Apresentar reclamação à ANPD</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-800">9. Cookies</h2>
            <p className="text-slate-600">
              Utilizamos cookies essenciais para o funcionamento da plataforma e cookies de 
              análise para melhorar a experiência do usuário. Você pode configurar seu navegador 
              para recusar cookies, mas isso pode afetar a funcionalidade do serviço.
            </p>

            <h2 className="text-xl font-bold text-slate-800">10. Transferência Internacional</h2>
            <p className="text-slate-600">
              Alguns de nossos prestadores de serviços podem estar localizados fora do Brasil. 
              Nesses casos, garantimos que a transferência ocorra com níveis adequados de proteção, 
              conforme exigido pela LGPD.
            </p>

            <h2 className="text-xl font-bold text-slate-800">11. Alterações na Política</h2>
            <p className="text-slate-600">
              Esta política pode ser atualizada periodicamente. Notificaremos os usuários sobre 
              alterações significativas por email ou aviso na plataforma. O uso continuado após 
              as alterações constitui aceitação da nova política.
            </p>

            <h2 className="text-xl font-bold text-slate-800">12. Encarregado de Dados (DPO)</h2>
            <p className="text-slate-600">
              Para exercer seus direitos ou esclarecer dúvidas sobre privacidade, entre em contato 
              com nosso Encarregado de Proteção de Dados:
            </p>
            <div className="bg-slate-100 p-4 rounded-lg">
              <p className="text-slate-600 mb-0">
                <strong>Razão Social:</strong> Karina Di Paula Santana Soares LTDA<br/>
                <strong>Nome Fantasia:</strong> Ogunhê Desenvolvimento de Sistemas<br/>
                <strong>CNPJ:</strong> 66.298.458/0001-87<br/>
                <strong>Inscrição Municipal:</strong> 2798824<br/>
                <strong>Email:</strong> <a href="mailto:privacidade@cerpbrasil.com.br" className="text-teal-600">privacidade@cerpbrasil.com.br</a><br/>
                <strong>Endereço:</strong> Rua Paes Leme, 215, Conj 1713 - Pinheiros, São Paulo - SP, CEP 05424-150
              </p>
            </div>

            <div className="mt-8 p-4 bg-teal-50 rounded-lg border border-teal-200">
              <p className="text-sm text-teal-700 mb-0">
                <strong>Versão:</strong> 1.0 | <strong>Data:</strong> {new Date().toLocaleDateString('pt-BR')} | 
                <strong> Em conformidade com a LGPD (Lei nº 13.709/2018)</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/termos-de-uso">
            <Button variant="outline" className="mr-4">
              Ver Termos de Uso
            </Button>
          </Link>
          <Link to="/login">
            <Button className="bg-teal-600 hover:bg-teal-700">
              Fazer Login
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PoliticaDePrivacidade;
